import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Sun,
  Moon,
  Menu,
  X,
  LayoutDashboard,
  Sparkles,
  Scale,
} from '@/components/icons';
import { useTheme } from '@/components/theme-provider';
import { useAuth } from '@/contexts/auth-context';
import { LanguageSwitcher } from '@/components/common/language-switcher';
import { getLandingContent } from '@/pages/landing/content';
import { useSetupStatus } from '@/hooks/use-setup-status';
import { APP_VERSION } from '@/lib/app-version';
import { SocialLinks } from '@/components/common/social-icons';

const PRIMARY_LINK_BUTTON =
  'inline-flex min-h-9 items-center justify-center gap-1.5 sm:gap-2 rounded-lg bg-primary px-3.5 sm:px-4 text-sm font-semibold text-primary-foreground shadow-xs transition-all duration-150 hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary active:scale-[0.98]';
const SECONDARY_LINK_BUTTON =
  'inline-flex min-h-9 items-center justify-center rounded-lg border border-border bg-background px-3 sm:px-4 text-xs sm:text-sm font-medium shadow-xs transition-all duration-150 hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary active:scale-[0.98] dark:bg-card dark:hover:bg-secondary';

export const PublicLayout: React.FC = () => {
  const { theme, resolvedTheme, toggleTheme } = useTheme();
  const { isAuthenticated } = useAuth();
  const { data: setupStatus } = useSetupStatus();
  const signupsEnabled = setupStatus?.signups_enabled !== false;
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isLoginPage = location.pathname === '/login';
  const isRegisterPage = location.pathname === '/register';

  const content = getLandingContent(i18n.language);
  const isDark = (resolvedTheme || theme) === 'dark';

  const navItems = [
    { id: 'product', label: content.nav.product },
    { id: 'features', label: content.nav.features },
    { id: 'how-it-works', label: content.nav.workflow },
    { id: 'pricing', label: content.nav.pricing },
    { id: 'faq', label: content.nav.faq },
  ];

  const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const handleAnchor = (id: string) => (event: React.MouseEvent) => {
    event.preventDefault();
    setMobileMenuOpen(false);
    if (location.pathname === '/') {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    } else {
      navigate(`/#${id}`);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col antialiased selection:bg-primary/20 selection:text-primary">
      <a
        href="#main-content"
        className="sr-only z-50 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
      >
        {t('common.skip_to_content', 'Skip to main content')}
      </a>
      {/* Sticky Top Navbar */}
      <header className="sticky top-0 z-40 border-b border-border/80 bg-card/95 backdrop-blur shadow-2xs transition-colors">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between gap-2 h-16">
            <div className="flex shrink-0 items-center min-w-0">
              <Link to="/" className="flex items-center gap-2.5 shrink-0">
                <span className="flex items-center justify-center size-8 rounded-lg bg-primary text-primary-foreground shadow-xs">
                  <Building2 className="w-4.5 h-4.5" />
                </span>
                <span className="text-sm font-bold tracking-tight text-foreground">
                  Koinon&nbsp;
                  <span className="text-muted-foreground font-medium">Κοινόν</span>
                </span>
              </Link>
            </div>

            <nav
              className="hidden min-w-0 flex-1 items-center justify-center gap-0.5 lg:flex xl:gap-1.5"
              aria-label={t('common.primary_navigation', 'Primary navigation')}
            >
              {navItems.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={handleAnchor(item.id)}
                  className="whitespace-nowrap rounded-lg px-2.5 py-2 text-[13px] font-semibold text-foreground/80 transition-colors hover:bg-secondary hover:text-foreground xl:px-3"
                >
                  {item.label}
                </a>
              ))}
            </nav>

            <div className="flex shrink-0 items-center gap-2">
              <div className="hidden sm:flex items-center gap-1.5">
                {isAuthenticated ? (
                  <Link to="/app" className={PRIMARY_LINK_BUTTON}>
                    <LayoutDashboard className="h-4 w-4" />
                    {t('common.dashboard', 'Dashboard')}
                  </Link>
                ) : (
                  <>
                    {!isLoginPage && (
                      <Link
                        to="/login"
                        className="inline-flex h-9 items-center justify-center rounded-lg px-3.5 text-sm font-semibold text-foreground/80 transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        {content.nav.login}
                      </Link>
                    )}
                    {signupsEnabled && !isRegisterPage && (
                      <Link to="/register" className={PRIMARY_LINK_BUTTON}>
                        <Sparkles className="h-4 w-4" />
                        {content.nav.cta}
                      </Link>
                    )}
                  </>
                )}
              </div>

              <div className="hidden sm:block h-4 w-px bg-border/80 mx-0.5" aria-hidden="true" />

              <button
                type="button"
                onClick={toggleTheme}
                aria-label={t('docs.theme_toggle', 'Toggle theme')}
                className="inline-flex items-center justify-center size-9 rounded-lg border border-border/80 bg-background text-foreground/80 hover:text-foreground hover:bg-secondary transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary shadow-2xs"
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <div className="hidden sm:block">
                <LanguageSwitcher variant="navbar" />
              </div>

              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-expanded={mobileMenuOpen}
                aria-label={t('common.toggle_navigation', 'Toggle navigation')}
                className="lg:hidden inline-flex items-center justify-center size-9 rounded-lg border border-border/80 bg-background text-foreground/80 hover:text-foreground hover:bg-secondary transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary shadow-2xs"
              >
                {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-border/80 bg-card/95 backdrop-blur px-4 py-4 space-y-4">
            <nav className="flex flex-col gap-1" aria-label={t('common.mobile_navigation', 'Mobile navigation')}>
              {navItems.map((item) => (
                <a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={handleAnchor(item.id)}
                  className="px-3.5 py-2 rounded-lg text-sm font-semibold text-foreground/80 hover:bg-secondary hover:text-foreground transition-colors"
                >
                  {item.label}
                </a>
              ))}
            </nav>
            <div className="pt-2 border-t border-border flex items-center justify-between">
              <LanguageSwitcher />
            </div>
            <div className="pt-2 border-t border-border flex flex-col gap-2">
              {isAuthenticated ? (
                <Link
                  to="/app"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`${PRIMARY_LINK_BUTTON} w-full`}
                >
                  <LayoutDashboard className="h-4 w-4" />
                  {t('common.dashboard', 'Dashboard')}
                </Link>
              ) : (
                <>
                  {!isLoginPage && (
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)} className={`${SECONDARY_LINK_BUTTON} w-full`}>
                      {content.nav.login}
                    </Link>
                  )}
                  {signupsEnabled && !isRegisterPage && (
                    <Link to="/register" onClick={() => setMobileMenuOpen(false)} className={`${PRIMARY_LINK_BUTTON} w-full`}>
                      <Sparkles className="h-4 w-4" />
                      {content.nav.cta}
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Outlet */}
      <main id="main-content" tabIndex={-1} className="flex-1 flex flex-col focus-visible:outline-none">
        <Outlet />
      </main>

      {/* Public Marketing Footer */}
      <footer className="border-t border-border/80 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="grid gap-8 md:grid-cols-[1fr_auto_auto] md:gap-x-16">
            <div className="col-span-2 md:col-span-1 space-y-3">
              <Link to="/" className="flex items-center gap-2">
                <span className="flex items-center justify-center size-7 rounded-lg bg-primary text-primary-foreground">
                  <Building2 className="w-4 h-4" />
                </span>
                <span className="text-sm font-bold tracking-tight text-foreground">
                  Koinon&nbsp;
                  <span className="text-muted-foreground font-medium">Κοινόν</span>
                </span>
              </Link>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {content?.footer?.tagline}
              </p>
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <Scale className="w-3.5 h-3.5 text-primary" />
                <span>Property and community operations</span>
              </div>
              <SocialLinks className="pt-1" />
            </div>

            <div className="space-y-2.5">
              <p className="text-sm font-bold tracking-tight text-foreground">{content?.footer?.product || 'Product'}</p>
              <ul className="space-y-1 text-xs text-muted-foreground">
                {navItems.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      onClick={handleAnchor(item.id)}
                      className="inline-block py-1.5 hover:text-foreground transition-colors"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
                <li>
                  <Link to="/calculator" className="inline-block py-1.5 hover:text-foreground transition-colors">
                    {content.nav.calculator}
                  </Link>
                </li>
              </ul>
            </div>

            <div className="space-y-2.5">
              <p className="text-sm font-bold tracking-tight text-foreground">{content?.footer?.account || 'Account'}</p>
              <ul className="space-y-1 text-xs text-muted-foreground">
                {signupsEnabled && (
                  <li>
                    <Link to="/register" className="inline-block py-1.5 hover:text-foreground transition-colors">
                      {content.nav.cta}
                    </Link>
                  </li>
                )}
                <li>
                  <Link to="/login" className="inline-block py-1.5 hover:text-foreground transition-colors">
                    {content.nav.login}
                  </Link>
                </li>
              </ul>
            </div>

          </div>

          <div className="mt-10 pt-6 border-t border-border flex flex-col gap-2 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
            <p>{content?.footer?.copyright}</p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <Link to="/terms" className="text-[11px] py-1 inline-block hover:text-foreground transition-colors">
                {t('terms.footer_link', 'Terms of Service')}
              </Link>
              <Link to="/privacy" className="text-[11px] py-1 inline-block hover:text-foreground transition-colors">
                {t('privacy.footer_link', 'Privacy')}
              </Link>
              <p className="text-[11px] font-mono-numeric text-muted-foreground py-1">
                {t('common.app_version', { version: APP_VERSION })}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
